import cx from "classnames"
import moment from "moment"
import {useModel, useVisible} from "hooks"
import {Icon} from "components"
import {KlassDropdown} from "components"
import {useState} from "react"
import {calendarService} from "services"
import {handleError} from "helpers"
import styles from "./EventCard.module.css"
import {head} from "lodash"
import {Settings} from "types/settings"
import {useWorkflowTriggerOutcomeItems} from "hooks/useWorkflowTriggerOutcomeItems"
import {ConfirmPopup} from "uiKit"
import {useTranslation} from "react-i18next"

export const EventCard = (props: {eventInfo: any; eventOptions: any; outcomeEventTypes: any}) => {
    const {eventInfo, eventOptions, outcomeEventTypes} = props
    const model = useModel()
    const {t} = useTranslation(["calendarEvents", "common"])
    const {hasWorkflowTrigger} = useWorkflowTriggerOutcomeItems()
    const confirmPopup = useVisible()
    const [pendingOutcomeId, setPendingOutcomeId] = useState<number | null>(null)
    const {title, start, startDateTime, endDateTime, allDay, color, ownerFullName, reminders, json_data} = eventInfo
    const editEventInfo = eventOptions.find(
        (eventOption) => eventOption.icon === "EDIT" && (!eventOption.canShow || eventOption.canShow(eventInfo))
    )
    const outcomeGroup: Settings.OutcomeGroup = head(
        outcomeEventTypes?.data?.find((outcome) => outcome.eventTypeId === json_data?.eventTypeId)?.outcomeGroups
    )
    const outcomes = outcomeGroup?.outcomes || []

    const [activeOutcome, setActiveOutcome] = useState(json_data.completedOutcomeId)

    const updateOutcome = async (value: number) => {
        try {
            setActiveOutcome(value)
            await calendarService.updateObjectOutcome({object_id: +eventInfo.id, outcome_id: value})
        } catch (error) {
            handleError(error)
        }
    }

    const onChangeOutcome = (value: number) => {
        if (value && hasWorkflowTrigger(value)) {
            setPendingOutcomeId(value)
            confirmPopup.open()
            return
        }
        updateOutcome(value)
    }

    const handleConfirmOutcomeChange = () => {
        if (pendingOutcomeId) {
            updateOutcome(pendingOutcomeId)
        }
        setPendingOutcomeId(null)
        confirmPopup.close()
    }

    const handleCancelOutcomeChange = () => {
        setPendingOutcomeId(null)
        confirmPopup.close()
    }

    return (
        <>
            <div
                className={cx(styles.panelItemWrap, {[styles.cursor]: editEventInfo ? true : false})}
                style={{borderColor: color}}
                onClick={() => editEventInfo?.action(eventInfo)}>
                <div className={styles.panelItemTitleWrap}>
                    <Icon className={styles.panelItemIcon} icon="CALENDAR_INPUT" />
                    <div className={styles.panelItemTitle}>{title}</div>
                </div>
                <div className={styles.contentWrap}>
                    <div className={styles.contentTitle}>Date</div>
                    <div className={styles.contentValue}>{moment(start).format(model.getUserDateFormat())}</div>
                </div>
                <div className={styles.contentWrap}>
                    <div className={styles.contentTitle}>Time</div>
                    <div className={styles.contentValue}>
                        {allDay
                            ? "Whole Day"
                            : `${moment(startDateTime).format(model.getUserTimeFormat())} - ${moment(
                                  endDateTime
                              ).format(model.getUserTimeFormat())}`}
                    </div>
                </div>
                <div className={styles.contentWrap}>
                    <div className={styles.contentTitle}>Reminders</div>
                    <div
                        className={styles.contentValue}
                        dangerouslySetInnerHTML={{
                            __html: reminders?.map((reminder) => reminder.label).join("<br/>")
                        }}
                    />
                </div>
                <div className={styles.contentWrap}>
                    <div className={styles.contentTitle}>Owner</div>
                    <div className={styles.contentValue}>{ownerFullName}</div>
                </div>
                {model.isStaffOrAdmin() && (
                    <div className={styles.contentWrap}>
                        <div className={styles.contentTitle}>Outcome</div>
                        <div
                            className={styles.contentValue}
                            style={{flex: 1, marginLeft: 12}}
                            onClick={(e) => e.stopPropagation()}>
                            <KlassDropdown
                                options={outcomes}
                                value={outcomes?.find((outcome) => outcome.outcomeId === activeOutcome)}
                                valueKey="outcomeId"
                                onChange={(value) => onChangeOutcome(value?.outcomeId)}
                                placeholder="Select Outcome"
                                stylesCustom={{
                                    control: (styles) => ({
                                        ...styles,
                                        minHeight: "36px",
                                        fontSize: "14px"
                                    })
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
            <ConfirmPopup
                isVisible={confirmPopup.isVisible}
                title={t("calendarEvents.workflowTriggerConfirmTitle")}
                description={t("calendarEvents.workflowTriggerConfirmDescription")}
                onClose={handleCancelOutcomeChange}
                onConfirm={handleConfirmOutcomeChange}
            />
        </>
    )
}
